// src/app/pages/profilePage/ProfilePage.js
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import styles from "./ProfilePage.module.css";

import { usePerson } from "../../hooks/user/usePerson";
import { useZorni } from "../../hooks/useZorni";
import { useOperation } from "../../../dashboard/hooks/useOperation";
import {
  usePhoneValidation,
  buildPhoneValue,
} from "../../hooks/usePhoneValidation";
import {
  GET_PERSON,
  UPDATE_USER_MUTATION,
  updatePerson,
} from "./ProfilePageAPI";

import { useUpload } from "../../../common/hooks/useUpload";

import { useMutation, useQuery } from "@apollo/client";
import { Avatar, Breadcrumb, Button, Calendar, Icon, Input, InternationalPhoneInput, Modal, SectionHeader, SelectMenu, Text, UploadFile } from "@zorni/components-lib";

import { UPLOAD_RESOURCES, UPLOAD_TYPES } from "../../../common/model/model";
import {
  PROFILE_PHOTO_UPLOAD_EXTENSIONS,
  getAcceptAttribute,
} from "../../../common/upload/uploadFileRules";
import { useUploadFileGuard } from "../../../common/upload/useUploadFileGuard";
import UploadFileError from "../../../common/upload/UploadFileError";

// -----------------------------------------------------------------------------
// Helpers
// -----------------------------------------------------------------------------

const buildInitialForm = (person, fallbackEmail) => {
  if (!person) {
    return {
      picture: "",
      firstName: "",
      lastName: "",
      gender: "",
      dateOfBirth: "",
      email: fallbackEmail || "",
      governmentIDNumber: "",
      type: "",
      status: "",
    };
  }

  return {
    picture: person.picture || "",
    firstName: person.firstName || "",
    lastName: person.lastName || "",
    gender: person.gender || "",
    dateOfBirth: person.dateOfBirth || "",
    email: person.email || fallbackEmail || "",
    governmentIDNumber: person.governmentIDNumber || "",
    type: person.type || "",
    status: person.status || "",
  };
};

const extractErrorMessage = (error, fallback) => {
  if (!error) return fallback;

  const graphQLErrorMessage = error?.graphQLErrors?.[0]?.message;
  if (graphQLErrorMessage) return graphQLErrorMessage;

  const networkErrorMessage =
    error?.networkError?.result?.errors?.[0]?.message ||
    error?.networkError?.result?.error?.message ||
    error?.networkError?.message;
  if (networkErrorMessage) return networkErrorMessage;

  if (error?.message) return error.message;

  return fallback;
};

const formatDateForApi = (value) => {
  if (!value) return "";
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return "";

  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

// -----------------------------------------------------------------------------
// Composant principal
// -----------------------------------------------------------------------------

const ProfilePage = () => {
  const { t } = useTranslation();

  // 🔐 infos sûres depuis Keycloak
  const { userId, email, type, keycloak } = useZorni();

  // Contexte global personne (pour le reste du portail)
  const { personInfo, setPersonInfo } = usePerson();

  // Query GraphQL directe pour avoir TOUTES les infos à jour
  const {
    data: personData,
    loading: personLoading,
    error: personError,
  } = useQuery(GET_PERSON, {
    variables: { personId: userId },
    skip: !userId,
    fetchPolicy: "cache-and-network",
  });

  // On privilégie la donnée du backend, sinon le contexte, sinon null
  const person = personData?.QR_GET_PERSON?.data || personInfo || null;


  const [updatePersonMutation] = useMutation(UPDATE_USER_MUTATION);

  // Formulaire global
  const [form, setForm] = useState(() => buildInitialForm(person, email));
  const initialFormRef = useRef(buildInitialForm(person, email));

  // Téléphone : état + validation centralisés dans usePhoneValidation
  // (indicative/number, cf. src/app/hooks/usePhoneValidation.js)
  const phone = usePhoneValidation(person?.phoneNumber);

  const { showToast } = useOperation();
  const { uploadFile, updateFile, getUploadIdFromUrl } = useUpload();
  const [isSaving, setIsSaving] = useState(false);

  // modes édition par section
  const [editIdentity, setEditIdentity] = useState(false);
  const [editContact, setEditContact] = useState(false);
  const [editGovId, setEditGovId] = useState(false);

  // modale d’avatar
  const [isAvatarModalOpen, setIsAvatarModalOpen] = useState(false);
  const [avatarFiles, setAvatarFiles] = useState([]);
  // Photo de profil : JPEG, JPG et PNG uniquement (PDF refusé).
  const { guardOnUpload: guardAvatarUpload, fileError: avatarFileError } =
    useUploadFileGuard(PROFILE_PHOTO_UPLOAD_EXTENSIONS);
  const [avatarPreview, setAvatarPreview] = useState(null);
  const [isAvatarSaving, setIsAvatarSaving] = useState(false);

  // Re-sync quand les données backend arrivent / changent
  useEffect(() => {
    if (person || email) {
      const fresh = buildInitialForm(person, email);
      setForm(fresh);
      initialFormRef.current = fresh;
      phone.reset(person?.phoneNumber);
    }
    // phone.reset is stable (useCallback, no deps) — safe to omit from deps.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [person, email]);

  const isDirty = useMemo(() => {
    return (
      JSON.stringify(form) !== JSON.stringify(initialFormRef.current) ||
      phone.value !== buildPhoneValue(person?.phoneNumber)
    );
  }, [form, phone.value, person]);

  const hasActiveEdition = editIdentity || editContact || editGovId;

  const displayName =
    form.firstName || form.lastName
      ? `${form.firstName || ""} ${form.lastName || ""}`.trim()
      : t("profile.anonymous", "Voyageur Zorni");

  const genderOptions = [
    { value: "MALE", text: "MALE" },
    { value: "FEMALE", text: "FEMALE" },
  ];

  // ---------------------------------------------------------------------------
  // Loading / erreur
  // ---------------------------------------------------------------------------

  if (personLoading && !person) {
    return (
      <div className={styles.pageContainer}>
        <Text type="text" size="md">
          {t("loading", "Chargement en cours...")}
        </Text>
      </div>
    );
  }

  if (!person && personError) {
    return (
      <div className={styles.pageContainer}>
        <Text type="text" size="md" color="danger">
          {t(
            "profile.loadError",
            "Impossible de charger vos informations pour le moment."
          )}
        </Text>
      </div>
    );
  }

  if (!person) {
    return (
      <div className={styles.pageContainer}>
        <Text type="text" size="md">
          {t("loading", "Chargement en cours...")}
        </Text>
      </div>
    );
  }

  // ---------------------------------------------------------------------------
  // Handlers génériques
  // ---------------------------------------------------------------------------

  const handleChange = (field) => (eOrValue) => {
    const newValue =
      eOrValue && eOrValue.target ? eOrValue.target.value : eOrValue;
    setForm((prev) => ({ ...prev, [field]: newValue }));
  };


  const handlePasswordUpdate = () => {
    const authServerUrl = (keycloak?.authServerUrl || "").replace(/\/$/, "");
    const realm = keycloak?.realm;
    const clientId = keycloak?.clientId;
    const redirectUri = window.location.origin;

    if (!authServerUrl || !realm || !clientId) return;

    const passwordActionUrl = `${authServerUrl}/realms/${realm}/protocol/openid-connect/auth?client_id=${encodeURIComponent(
      clientId
    )}&redirect_uri=${encodeURIComponent(
      redirectUri
    )}&response_type=code&scope=openid&kc_action=UPDATE_PASSWORD&prompt=login`;

    window.location.assign(passwordActionUrl);
  };

  const handleCancelAll = () => {
    const reset = initialFormRef.current;
    setForm(reset);
    phone.reset(person?.phoneNumber);
    setEditIdentity(false);
    setEditContact(false);
    setEditGovId(false);
  };

  const handleSubmit = async () => {
    if (!person || !userId) return;

    if (!phone.validate()) {
      showToast(
        t(
          "profile.phoneInvalid",
          "Le numéro de téléphone est invalide. Corrigez-le avant d'enregistrer."
        ),
        "error"
      );
      return;
    }

    setIsSaving(true);
    try {
      const personInput = {
        id: person.id,
        picture: form.picture || null,
        firstName: form.firstName || null,
        lastName: form.lastName || null,
        gender: form.gender || null,
        dateOfBirth: form.dateOfBirth || null,
        // ⚠️ email = celui du token Keycloak, pas modifiable depuis le front
        email: email || form.email || null,
        phoneNumber: phone.phoneNumber,
        governmentIDNumber: form.governmentIDNumber || null,
        type: form.type || type || null,
        status: form.status || null,
        operation: person?.operation || "CREATION",
      };

      const { data, error } = await updatePerson(
        updatePersonMutation,
        userId,
        personInput
      );

      if (error) {
        showToast(
          extractErrorMessage(
            error,
            t(
              "profile.saveError",
              "Impossible d'enregistrer vos modifications pour le moment."
            )
          ),
          "error"
        );
        return;
      }

      if (data?.MT_UPDATE_PERSON?.error?.message) {
        showToast(data.MT_UPDATE_PERSON.error.message, "error");
        return;
      }

      if (data?.MT_UPDATE_PERSON?.data) {
        const updated = data.MT_UPDATE_PERSON.data;

        // Met à jour le contexte global + le form local
        setPersonInfo(updated);
        const fresh = buildInitialForm(updated, email);
        initialFormRef.current = fresh;
        setForm(fresh);

        setEditIdentity(false);
        setEditContact(false);
            setEditGovId(false);
        showToast(
          t("profile.saveSuccess", "Votre profil a été mis à jour."),
          "success"
        );
      } else {
        showToast(
          t(
            "profile.saveError",
            "Impossible d'enregistrer vos modifications pour le moment."
          ),
          "error"
        );
      }
    } catch (err) {
      console.error("Erreur de mise a jour du profil :", err);
      showToast(
        extractErrorMessage(
          err,
          t(
            "profile.saveError",
            "Impossible d'enregistrer vos modifications pour le moment."
          )
        ),
        "error"
      );
    } finally {
      setIsSaving(false);
    }
  };

  // ---------------------------------------------------------------------------
  // Avatar / photo de profil
  // ---------------------------------------------------------------------------

  const openAvatarModal = () => {
    setAvatarFiles([]);
    setAvatarPreview(null);
    setIsAvatarModalOpen(true);
  };

  const closeAvatarModal = () => {
    setIsAvatarModalOpen(false);
    setAvatarFiles([]);
    setAvatarPreview(null);
    setIsAvatarSaving(false);
  };

  const handleAvatarUploadChange = (files) => {
    setAvatarFiles(files || []);
    if (files && files.length > 0) {
      const file = files[files.length - 1];
      const reader = new FileReader();
      reader.onload = () => setAvatarPreview(reader.result);
      reader.readAsDataURL(file);
    } else {
      setAvatarPreview(null);
    }
  };

  const handleAvatarSave = async () => {
    if (!avatarFiles.length || !userId) return;
    const file = avatarFiles[avatarFiles.length - 1];

    setIsAvatarSaving(true);
    try {
      let result;
      if (!form.picture) {
        result = await uploadFile(
          file,
          UPLOAD_RESOURCES.PERSON,
          UPLOAD_TYPES.PHOTO
        );
      } else {
        const uploadId = getUploadIdFromUrl(form.picture);
        result = await updateFile(
          uploadId,
          file,
          UPLOAD_RESOURCES.PERSON,
          UPLOAD_TYPES.PHOTO
        );
      }

      const fileLink = result?.fileLink;
      if (fileLink) {
        // Le backend remplace l'objet personne entier (pas de merge partiel) :
        // on renvoie tout le formulaire, pas seulement la photo, sinon un
        // simple changement d'avatar écraserait firstName/lastName/téléphone/etc.
        const personInput = {
          id: userId,
          picture: fileLink,
          firstName: form.firstName || null,
          lastName: form.lastName || null,
          gender: form.gender || null,
          dateOfBirth: form.dateOfBirth || null,
          email: email || form.email || null,
          phoneNumber: phone.phoneNumber,
          governmentIDNumber: form.governmentIDNumber || null,
          type: form.type || type || null,
          status: form.status || null,
          operation: person?.operation || "CREATION",
        };
        const { data, error } = await updatePerson(
          updatePersonMutation,
          userId,
          personInput
        );

        if (!error && data?.MT_UPDATE_PERSON?.data) {
          const updated = data.MT_UPDATE_PERSON.data;
          setPersonInfo(updated);
          const fresh = buildInitialForm(updated, email);
          initialFormRef.current = fresh;
          setForm(fresh);
        } else {
          // fallback local si jamais la mutation ne renvoie rien d'exploitable
          setForm((prev) => ({ ...prev, picture: fileLink }));
        }
        showToast(
          t("profile.avatarSaveSuccess", "Votre photo de profil a été mise à jour."),
          "success"
        );
      } else {
        showToast(
          t(
            "profile.avatarSaveError",
            "Impossible de mettre à jour votre photo de profil pour le moment."
          ),
          "error"
        );
      }
    } catch (err) {
      console.error(
        "Une erreur est survenue lors de la mise à jour de l'avatar.",
        err
      );
      showToast(
        t(
          "profile.avatarSaveError",
          "Impossible de mettre à jour votre photo de profil pour le moment."
        ),
        "error"
      );
    } finally {
      setIsAvatarSaving(false);
      closeAvatarModal();
    }
  };

  // ---------------------------------------------------------------------------
  // RENDER
  // ---------------------------------------------------------------------------

  return (
    <div className={styles.page}>
      <Breadcrumb
        className={styles.breadcrumb}
        ariaLabel={t("trips.results.breadcrumb", "Fil d'Ariane")}
        linkComponent={Link}
        items={[
          { label: t("trips.results.home", "Accueil"), to: "/" },
          { label: t("profile.title") },
        ]}
      />

      <SectionHeader
        size="page"
        className={styles.pageHeader}
        kicker={t("profile.kicker", "Vos informations")}
        title={t("profile.title")}
        subtitle={t(
          "profile.subtitle",
          "Gérez vos informations personnelles et vos coordonnées."
        )}
      />

      {/* Carte d'identité visuelle, comme « card-identity » dans la maquette. */}
      <section className={`${styles.card} ${styles.identityCard}`}>
        <div className={styles.profileHeroAvatarWrap}>
          <Avatar
            size="lg"
            name={displayName}
            src={form.picture}
            dot={false}
            className={styles.profileHeroAvatar}
          />
          <button
            type="button"
            className={styles.profileHeroAvatarAction}
            onClick={openAvatarModal}
            aria-label={t("profile.actions.changePicture")}
          >
            <Icon name="iconUpload" size="sm" />
          </button>
        </div>

        <div className={styles.profileHeroIdentity}>
          <Text as="span" size="md" weight="medium" color="default">
            {form.firstName || person?.firstName || t("profile.anonymous", "Voyageur")}
          </Text>
          <Text as="h2" size="xl" weight="semibold">
            {String(form.lastName || person?.lastName || "").toUpperCase() ||
              t("profile.anonymous", "ZORNI")}
          </Text>
          <Text size="sm" color="subtle">
            {email || form.email}
          </Text>
        </div>
      </section>

      {/* GRID LAYOUT */}
      <div className={styles.grid}>
        {/* Colonne principale */}
        <main className={styles.mainColumn}>
          {/* SECTION – Informations personnelles */}
          <section className={styles.card}>
            <SectionHeader
              size="card"
              className={styles.sectionHeader}
              title={t("profile.sections.identity.title")}
              subtitle={t("profile.sections.identity.helper")}
              actions={
                <Button
                  variant="ghost"
                  size="small"
                  iconName={editIdentity ? "iconClose" : "iconPenToSquare"}
                  iconPosition="left"
                  label={t(
                    editIdentity
                      ? "commons.buttons.cancel"
                      : "commons.allActions.edit"
                  )}
                  onClick={() => setEditIdentity((v) => !v)}
                />
              }
            />

            <div className={styles.sectionBodyGrid}>
              {/* Prénom */}
              <div className={styles.fieldGroup}>
                <Text
                  size="xs"
                  weight="medium"
                  color="subtle"
                  className={styles.fieldLabel}
                >
                  {t("firstName")}
                </Text>
                {editIdentity ? (
                  <Input
                    name="firstName"
                    value={form.firstName}
                    onChange={handleChange("firstName")}
                    placeholder={t("firstName")}
                    floatingLabel={false}
                    inputSize="medium"
                  />
                ) : (
                  <Text className={styles.readonlyValue}>
                    {form.firstName || (
                      <Text isNotSpecified>{t("NotSpecified")}</Text>
                    )}
                  </Text>
                )}
              </div>

              {/* Nom */}
              <div className={styles.fieldGroup}>
                <Text
                  size="xs"
                  weight="medium"
                  color="subtle"
                  className={styles.fieldLabel}
                >
                  {t("lastName")}
                </Text>
                {editIdentity ? (
                  <Input
                    name="lastName"
                    value={form.lastName}
                    onChange={handleChange("lastName")}
                    placeholder={t("lastName")}
                    floatingLabel={false}
                    inputSize="medium"
                  />
                ) : (
                  <Text className={styles.readonlyValue}>
                    {form.lastName || (
                      <Text isNotSpecified>{t("NotSpecified")}</Text>
                    )}
                  </Text>
                )}
              </div>

              {/* Genre */}
              <div className={styles.fieldGroup}>
                <Text
                  size="xs"
                  weight="medium"
                  color="subtle"
                  className={styles.fieldLabel}
                >
                  {t("gender")}
                </Text>
                {editIdentity ? (
                  <SelectMenu t={t}
                    name="gender"
                    value={form.gender}
                    onChange={(value) =>
                      setForm((prev) => ({ ...prev, gender: value }))
                    }
                    options={genderOptions}
                    placeholder={t("gender")}
                    inputSize="medium"
                  />
                ) : (
                  <Text className={styles.readonlyValue}>
                    {form.gender || (
                      <Text isNotSpecified>{t("NotSpecified")}</Text>
                    )}
                  </Text>
                )}
              </div>

              {/* Date de naissance */}
              <div className={styles.fieldGroup}>
                <Text
                  size="xs"
                  weight="medium"
                  color="subtle"
                  className={styles.fieldLabel}
                >
                  {t("dateOfBirth")}
                </Text>
                {editIdentity ? (
                  <Calendar
                    type="naissance"
                    withInput
                    inputLabel={t("dateOfBirth")}
                    inputSize="medium"
                    iconName="iconCalendar"
                    iconPosition="left"
                    value={form.dateOfBirth || null}
                    onChange={(date) =>
                      setForm((prev) => ({
                        ...prev,
                        dateOfBirth: formatDateForApi(date),
                      }))
                    }
                  />
                ) : (
                  <Text className={styles.readonlyValue}>
                    {form.dateOfBirth || (
                      <Text isNotSpecified>{t("NotSpecified")}</Text>
                    )}
                  </Text>
                )}
              </div>
            </div>
          </section>
        </main>

        {/* Colonne droite – Contact & sécurité */}
        <aside className={styles.sideColumn}>
          {/* Contact */}
          <section className={styles.card}>
            <SectionHeader
              size="card"
              className={styles.sectionHeader}
              title={t("profile.sections.contact.title")}
              subtitle={t("profile.sections.contact.helper")}
              actions={
                <Button
                  variant="ghost"
                  size="small"
                  iconName={editContact ? "iconClose" : "iconPenToSquare"}
                  iconPosition="left"
                  label={t(
                    editContact
                      ? "commons.buttons.cancel"
                      : "commons.allActions.edit"
                  )}
                  onClick={() => setEditContact((v) => !v)}
                />
              }
            />

            <div className={styles.sectionBodyColumn}>
              {/* Email (readonly, géré par Keycloak) */}
              <div className={styles.fieldGroup}>
                <Text
                  size="xs"
                  weight="medium"
                  color="subtle"
                  className={styles.fieldLabel}
                >
                  {t("email")}
                </Text>
                <Text className={styles.readonlyValue}>
                  {email || form.email || (
                    <Text isNotSpecified>{t("NotSpecified")}</Text>
                  )}
                </Text>
              </div>

              {/* Téléphone */}
              <div className={styles.fieldGroup}>
                <Text
                  size="xs"
                  weight="medium"
                  color="subtle"
                  className={styles.fieldLabel}
                >
                  {t("phoneNumber")}
                </Text>
                {editContact ? (
                  <InternationalPhoneInput
                    name="phone"
                    value={phone.value}
                    onChange={phone.onChange}
                    placeholder={t("phoneNumber")}
                    inputSize="medium"
                    defaultCountry="MA"
                    validate={false}
                    errors={[phone.errors.indicative, phone.errors.number]
                      .filter(Boolean)
                      .map((err) =>
                        t(`phoneValidation.${err.code}`, err.message)
                      )}
                  />
                ) : (
                  <Text className={styles.readonlyValue}>
                    {phone.value || (
                      <Text isNotSpecified>{t("NotSpecified")}</Text>
                    )}
                  </Text>
                )}
              </div>
            </div>
          </section>

          <section className={styles.card}>
            <SectionHeader
              size="card"
              className={styles.sectionHeader}
              title={t("profile.sections.password.title", "Mot de passe")}
              subtitle={t("profile.sections.password.helper", "Changez régulièrement votre mot de passe pour sécuriser votre compte.")}
            />

            <div className={styles.sectionBodyColumn}>
              <Button
                variant="secondary"
                size="medium"
                iconName="iconLock"
                iconPosition="left"
                label={t("profile.sections.password.action", "Changer mon mot de passe")}
                onClick={handlePasswordUpdate}
              />
            </div>
          </section>

          {/* Sécurité / ID gouvernemental */}
          {/* <section className={styles.card}>
            <SectionHeader
              size="card"
              className={styles.sectionHeader}
              title={t("profile.sections.security.title")}
              subtitle={t("profile.sections.security.helper")}
              actions={
                <Button
                  variant="ghost"
                  size="small"
                  iconName={editGovId ? "iconClose" : "iconPenToSquare"}
                  iconPosition="left"
                  label={t(
                    editGovId ? "commons.buttons.cancel" : "commons.allActions.edit"
                  )}
                  onClick={() => setEditGovId((v) => !v)}
                />
              }
            />

            <div className={styles.sectionBodyColumn}>
              <div className={styles.fieldGroup}>
                <Text
                  size="xs"
                  weight="medium"
                  color="subtle"
                  className={styles.fieldLabel}
                >
                  {t("governmentID")}
                </Text>
                {editGovId ? (
                  <Input
                    name="governmentIDNumber"
                    value={form.governmentIDNumber}
                    onChange={handleChange("governmentIDNumber")}
                    placeholder={t("governmentID")}
                    inputSize="medium"
                  />
                ) : (
                  <Text className={styles.readonlyValue}>
                    {form.governmentIDNumber ? (
                      form.governmentIDNumber
                    ) : (
                      <Text isNotSpecified>{t("NotSpecified")}</Text>
                    )}
                  </Text>
                )}
              </div>
            </div>
          </section> */}
        </aside>
      </div>

      {/* BARRE BAS – boutons principaux */}
      <div className={styles.bottomBar}>
        <Button
          variant="ghost"
          size="medium"
          label={t("commons.buttons.cancel")}
          onClick={handleCancelAll}
          disabled={!isDirty && !hasActiveEdition && !isAvatarSaving && !isSaving}
        />
        <Button
          variant="primary"
          size="medium"
          label={t("commons.buttons.submit")}
          onClick={handleSubmit}
          disabled={(!isDirty && !hasActiveEdition) || isSaving}
        />
      </div>

      {/* MODALE AVATAR */}
      <Modal t={t}
        isOpen={isAvatarModalOpen}
        onClose={closeAvatarModal}
        title={t("profile.actions.changePicture", "Modifier la photo")}
        size="sm"
      >
        <div className={styles.avatarModalContent}>
          <div className={styles.avatarModalPreview}>
            <Avatar
              size="lg"
              name={displayName}
              src={avatarPreview || form.picture}
            />
          </div>

          <UploadFile
            t={t}
            fileList={avatarFiles}
            onUpload={guardAvatarUpload(handleAvatarUploadChange)}
            showFiles={false}
            accept={getAcceptAttribute(PROFILE_PHOTO_UPLOAD_EXTENSIONS)}
          />
          <UploadFileError message={avatarFileError} />

          <div className={styles.avatarModalActions}>
            <Button
              variant="ghost"
              size="small"
              label={t("commons.buttons.cancel")}
              onClick={closeAvatarModal}
              disabled={isAvatarSaving}
            />
            <Button
              variant="primary"
              size="small"
              label={t("commons.buttons.submit")}
              onClick={handleAvatarSave}
              disabled={isAvatarSaving || avatarFiles.length === 0}
            />
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default ProfilePage;
